import React from 'react';
import { Link } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { Code2 } from 'lucide-react';
import { Marquee } from './marquee';
import { cn } from '../../lib/utils';

const footerLinkGroups = [
  {
    label: 'Explore',
    links: [
      { title: 'Services', href: '/services' },
      { title: 'About', href: '/about' },
      { title: 'Start a Project', href: '/start-project' },
    ],
  },
  {
    label: 'Legal',
    links: [
      { title: 'Privacy Policy', href: '/privacy-policy' },
      { title: 'Terms of Service', href: '/terms-of-service' },
    ],
  },
];

function FooterLinkItem({ link }) {
  const Icon = link.icon;
  const isInternal = typeof link.href === 'string' && link.href.startsWith('/') && !link.href.startsWith('/#');
  const baseClass =
    'inline-flex items-center text-white/68 hover:text-white transition-all duration-300';

  if (isInternal) {
    return (
      <Link to={link.href} className={baseClass}>
        {Icon && <Icon className="me-1 size-4" aria-hidden />}
        {link.title}
      </Link>
    );
  }
  return (
    <a href={link.href} className={baseClass}>
      {Icon && <Icon className="me-1 size-4" aria-hidden />}
      {link.title}
    </a>
  );
}

function AnimatedContainer({ delay = 0.1, children, className, ...props }) {
  const shouldReduceMotion = useReducedMotion();

  if (shouldReduceMotion) {
    return <div className={className}>{children}</div>;
  }

  return (
    <motion.div
      initial={{ filter: 'blur(4px)', translateY: -8, opacity: 0 }}
      whileInView={{ filter: 'blur(0px)', translateY: 0, opacity: 1 }}
      viewport={{ once: true }}
      transition={{ delay, duration: 0.8 }}
      className={className}
      {...props}
    >
      {children}
    </motion.div>
  );
}

export function StickyFooter({ className, ...props }) {
  const year = new Date().getFullYear();

  return (
    <footer
      className={cn('relative h-[840px] w-full text-white', className)}
      style={{ clipPath: 'polygon(0% 0, 100% 0%, 100% 100%, 0 100%)' }}
      {...props}
    >
      <div className="fixed bottom-0 h-[840px] w-full bg-black">
        <div className="h-full overflow-hidden">
          <div className="relative flex size-full flex-col justify-between gap-5 border-t border-white/10 px-4 py-8 md:px-12">
            <div
              aria-hidden
              className="absolute inset-0 isolate z-0 [contain:strict]"
            >
              <div className="absolute top-0 left-0 h-[1280px] w-[560px] -translate-y-[350px] -rotate-45 rounded-full bg-[radial-gradient(68.54%_68.72%_at_55.02%_31.46%,rgba(255,255,255,0.06)_0,rgba(255,255,255,0.02)_50%,rgba(255,255,255,0.01)_80%)]" />
              <div className="absolute top-0 left-0 h-[1280px] w-[240px] -translate-y-[50%] translate-x-[5%] -rotate-45 rounded-full bg-[radial-gradient(50%_50%_at_50%_50%,rgba(255,255,255,0.04)_0,rgba(255,255,255,0.01)_80%,transparent_100%)]" />
              <div className="absolute top-0 left-0 h-[1280px] w-[240px] -translate-y-[350px] -rotate-45 rounded-full bg-[radial-gradient(50%_50%_at_50%_50%,rgba(255,255,255,0.04)_0,rgba(255,255,255,0.01)_80%,transparent_100%)]" />
            </div>

            <div className="relative z-10 w-full shrink-0">
              <Marquee
                text="FuturoCode"
                repeat={12}
                duration={28}
                fontSize="lg"
                strokeWidth="1.25px"
                outline="bright"
                className="py-8 md:py-10"
              />
            </div>

            <div className="relative z-10 mt-2 flex flex-col gap-8 md:flex-row xl:mt-0">
              <AnimatedContainer className="w-full max-w-sm min-w-[18rem] space-y-4">
                <div className="flex items-center gap-2">
                  <Code2 className="size-7 text-[#47b0ff]" aria-hidden />
                  <span className="text-xl font-bold tracking-tight text-white">
                    FuturoCode
                  </span>
                </div>
                <p className="text-sm text-white/60 mt-8 md:mt-0">
                  Modern websites, brand systems, and digital experiences engineered to turn visitors into customers.
                </p>
              </AnimatedContainer>

              {footerLinkGroups.map((group, index) => (
                <AnimatedContainer
                  key={group.label}
                  delay={0.1 + index * 0.1}
                  className="w-full"
                >
                  <div className="mb-10 md:mb-0">
                    <h3 className="text-sm uppercase tracking-wider text-white">
                      {group.label}
                    </h3>
                    <ul className="mt-4 space-y-2 text-sm md:text-xs lg:text-sm">
                      {group.links.map((link) => (
                        <li key={link.title}>
                          <FooterLinkItem link={link} />
                        </li>
                      ))}
                    </ul>
                  </div>
                </AnimatedContainer>
              ))}
            </div>

            <div className="relative z-10 flex flex-col items-center justify-between gap-2 border-t border-white/10 pt-4 text-sm text-white/68 md:flex-row">
              <p>© {year} FuturoCode. All rights reserved.</p>
              <p>Crafted with care.</p>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default StickyFooter;
